// What a person is allowed to *do* in a clinic — the single source of truth
// shared by the backend guards (`src/http/middleware/auth.ts`) and the frontend
// role editor (`frontend/src/lib/permissions.ts`), both of which re-export from
// here. Mirrors how `@synapse/item-types` and `@synapse/analytics-contract` are
// wired.
//
// This package is inert: no drizzle, no Zod, no React, no I/O. It is the key
// list, the template role definitions, and pure predicates over them. Anything
// that throws the project's errors, or touches a table, or needs a translator
// belongs on one side or the other.
//
// The keys live in code rather than a `permissions` table on purpose. A guard
// names one as a literal — `requirePermission("audit.read")` — and a typo there
// has to be a compile error at every one of ~50 call sites, which a table
// cannot do. Adding a key always accompanies the route it guards, so a table
// would mean a migration and a code change kept in lockstep by discipline.
// Write-time integrity comes from `z.enum(PERMISSION_KEYS)` at the API
// boundary; read-time from `isPermissionKey` (see its comment for why reads
// must filter rather than reject).

export const PERMISSION_KEYS = [
  // Patient demographics. Distinct from the clinical record below: a
  // receptionist registers patients without ever seeing a consultation.
  "patients.read",
  "patients.create",
  "patients.update",
  "patients.delete",
  "patients.photo.manage",

  // The clinical record — encrypted consultation payloads and history. Every
  // read here writes a `phi_access_log` row.
  "clinical.consultations.read",
  "clinical.consultations.write",
  "clinical.consultations.finalize",
  "clinical.history.read",
  "clinical.history.write",

  // Typing up a visit from the doctor's paper: the diagnosis as written, what
  // was dispensed and what was prescribed to buy elsewhere. Its own key rather
  // than `clinical.consultations.write`, because the person holding it is a
  // transcriber, not a reader — it opens the one visit being recorded or
  // corrected and nothing of the patient's history. It also dispenses the
  // visit's medicines from stock in the same save, so it does not additionally
  // require `inventory.stock.issue`.
  "visits.record",

  // Inventory. Split by ledger effect, not by table: reading the catalogue,
  // receiving stock, issuing it and correcting history are four different
  // amounts of trust.
  "inventory.items.read",
  "inventory.items.create",
  "inventory.stock.in",
  "inventory.stock.issue",
  "inventory.stock.adjust",
  "inventory.movements.read",
  "inventory.movements.correct",

  // The lab desk — requisitions, specimens and report PDFs.
  //
  // `lab.results.read` is deliberately not folded into `lab.read`: booking a
  // patient and labelling a tube is front-desk work, while the report is where
  // the diagnosis is. A clinic that wants one person doing both grants both.
  "lab.read",
  "lab.orders.create",
  "lab.orders.update",
  "lab.specimens.collect",
  "lab.results.upload",
  "lab.results.read",
  "lab.results.delete",
  // Both of the lab desk's reference lists: the tests the clinic offers and the
  // laboratories it sends work to. One key, because no plausible role curates
  // one and not the other — see the comment in lab.routes.ts.
  "lab.catalogue.manage",

  "dashboard.read",
  "dashboard.activity.read",

  "apt_counter.read",
  "apt_counter.manage",

  "self_registrations.read",
  "self_registrations.review",

  "suggestions.read",

  // Oversight — "who dispensed what, when". Surveillance over colleagues, which
  // is why it is not bundled with the operational inventory keys.
  "analytics.read",
  "audit.read",

  // The clinic's own identity. Renaming it also changes its slug, which is the
  // first segment of every URL the clinic uses and is printed on its QR codes —
  // so this is a strictly bigger decision than editing any record inside it, and
  // gets its own key rather than riding along with `members.*`.
  "clinic.update",

  "members.read",
  "members.create",
  "members.update",
  "members.remove",

  "invitations.read",
  "invitations.create",
  "invitations.revoke",

  // Access control itself.
  //
  // `roles.manage` deliberately covers BOTH role CRUD and assigning roles to
  // members. Splitting it into `roles.manage` + `roles.assign` opens a hole: a
  // holder of `assign` alone could unassign the last `manage` holder, and the
  // clinic-lockout invariant would then have to reason about two keys at once.
  // One key, one invariant.
  "roles.read",
  "roles.manage",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

const PERMISSION_KEY_SET: ReadonlySet<string> = new Set(PERMISSION_KEYS);

/**
 * True for permission keys this build knows.
 *
 * Reads must filter through this rather than reject on a miss. A stored role
 * can outlive the key it holds — a release that retires a key leaves the string
 * sitting in `clinic_roles.permissions` rows and in already-issued JWTs, and
 * neither of those may be allowed to lock a clinic out of its own app. Unknown
 * keys are inert: they grant nothing, they deny nothing, and they disappear the
 * next time the role is edited.
 *
 * The write path is the opposite and rejects outright, via
 * `z.enum(PERMISSION_KEYS)` in `src/modules/roles/roles.schemas.ts`. Storage
 * only ever gains keys this build understands.
 */
export function isPermissionKey(value: string): value is PermissionKey {
  return PERMISSION_KEY_SET.has(value);
}

/**
 * Presentation grouping for the role editor. Purely a UI concern — nothing in
 * the authorization path reads it — but it lives here so a new key cannot be
 * added without deciding where it renders. The package test asserts every key
 * appears in exactly one group, which is what makes that true.
 */
export const PERMISSION_GROUPS = [
  {
    id: "patients",
    keys: [
      "patients.read",
      "patients.create",
      "patients.update",
      "patients.delete",
      "patients.photo.manage",
    ],
  },
  {
    id: "clinical",
    keys: [
      "clinical.consultations.read",
      "clinical.consultations.write",
      "clinical.consultations.finalize",
      "clinical.history.read",
      "clinical.history.write",
      "visits.record",
    ],
  },
  {
    id: "inventory",
    keys: [
      "inventory.items.read",
      "inventory.items.create",
      "inventory.stock.in",
      "inventory.stock.issue",
      "inventory.stock.adjust",
      "inventory.movements.read",
      "inventory.movements.correct",
    ],
  },
  {
    id: "lab",
    keys: [
      "lab.read",
      "lab.orders.create",
      "lab.orders.update",
      "lab.specimens.collect",
      "lab.results.upload",
      "lab.results.read",
      "lab.results.delete",
      "lab.catalogue.manage",
    ],
  },
  {
    id: "frontDesk",
    keys: [
      "apt_counter.read",
      "apt_counter.manage",
      "self_registrations.read",
      "self_registrations.review",
      "suggestions.read",
    ],
  },
  {
    id: "insights",
    keys: ["dashboard.read", "dashboard.activity.read", "analytics.read", "audit.read"],
  },
  {
    id: "clinic",
    keys: ["clinic.update"],
  },
  {
    id: "people",
    keys: [
      "members.read",
      "members.create",
      "members.update",
      "members.remove",
      "invitations.read",
      "invitations.create",
      "invitations.revoke",
      "roles.read",
      "roles.manage",
    ],
  },
] as const satisfies readonly { id: string; keys: readonly PermissionKey[] }[];

export type PermissionGroupId = (typeof PERMISSION_GROUPS)[number]["id"];

export interface RoleTemplate {
  name: string;
  description: string;
  permissions: readonly PermissionKey[];
}

/** Name of the template every new clinic's creator is assigned. */
export const ADMIN_TEMPLATE_NAME = "Admin";

/**
 * The roles a clinic starts with — copied into `clinic_roles` at clinic
 * creation and then owned by the clinic. Nothing in the database records that a
 * row came from a template: seeded roles are renameable, editable and
 * deletable like any other. These are a starting point, not a fixture.
 *
 * The two sets are chosen to reproduce the pre-permissions behaviour exactly.
 * `Staff` is the surface that `requireActiveClinic` used to gate; `Admin` is
 * that plus everything `requireClinicRole("admin")` used to gate. A clinic that
 * migrates and never touches the roles page sees no change at all.
 */
export const ROLE_TEMPLATES = [
  {
    name: ADMIN_TEMPLATE_NAME,
    description: "Full access to clinical records, inventory, people and settings.",
    permissions: PERMISSION_KEYS,
  },
  {
    name: "Staff",
    description: "Dispensing, patient registration and the appointment counter.",
    permissions: [
      "patients.read",
      "patients.create",
      "patients.update",
      "patients.photo.manage",
      // Staff record the visit the doctor wrote down. They do not get the
      // `clinical.*` read keys alongside it.
      "visits.record",
      "inventory.items.read",
      "inventory.items.create",
      "inventory.stock.in",
      "inventory.stock.issue",
      "inventory.stock.adjust",
      "inventory.movements.read",
      "inventory.movements.correct",
      // The lab desk is staff work. `results.read` is included because a tech
      // must be able to open the report they just uploaded to confirm it is the
      // right one; `results.delete` and `catalogue.manage` are not, because
      // destroying a record and redefining the clinic's test list are both
      // decisions rather than tasks.
      "lab.read",
      "lab.orders.create",
      "lab.orders.update",
      "lab.specimens.collect",
      "lab.results.upload",
      "lab.results.read",
      "dashboard.read",
      "apt_counter.read",
      "apt_counter.manage",
      "self_registrations.read",
      "self_registrations.review",
      "suggestions.read",
    ],
  },
] as const satisfies readonly RoleTemplate[];

/**
 * Ceiling on roles assignable to one member. Bounds the JWT payload (each role
 * contributes to the embedded permission union) and the assignment query. Far
 * above anything a real clinic needs — it is a runaway guard, not a policy.
 */
export const MAX_ROLES_PER_MEMBER = 10;

/** The permission that gates access control itself. Named because the clinic-lockout invariant is written in terms of it. */
export const ROLES_MANAGE: PermissionKey = "roles.manage";
